/**
 * Uploads an image to the file bucket for use inside an email body, and returns a
 * **public, absolute** URL that will render in any recipient's mail client.
 *
 * ## Why this helper exists (read before changing it)
 *
 * Email images have two hard requirements that the other bucket-upload call sites in
 * this repo do NOT satisfy, which is exactly why they can't be reused here:
 *
 *  1. **Public (no-auth).** Recipients are anonymous — they cannot send our session
 *     cookie. An image on the authed path (`/api3.2/bucket/:id`) shows as broken for
 *     every recipient. We must upload with `is_public='true'` and serve from the
 *     no-auth path (`/api3.2/noauth/bucket/:id`).
 *  2. **Absolute.** A relative `/api3.2/...` URL is meaningless once the HTML leaves
 *     our origin and is opened in Gmail/Outlook/etc. It must be fully qualified.
 *
 * Note that the upload response's own `url` field is always the *authed* relative path
 * (`/api3.2/bucket/:id`), even for public files — so we deliberately ignore it and build
 * the public URL from the returned `id` ourselves.
 *
 * Uses the shared `axiosInstance` (session-expiry handling, per the error-handling
 * standard) rather than a raw `fetch`, and **re-throws on failure** — it never returns
 * an empty string or a private URL as a fallback, because a silent fallback here means
 * broken images in already-delivered mail.
 */
import axiosInstance from 'api/axiosInstance';

/** Authed multipart upload endpoint. Returns `{ id, is_public, ... }`. */
const BUCKET_UPLOAD_ENDPOINT = '/api3.2/bucket/upload';

/** Shape of the fields we rely on from the bucket upload response. */
interface BucketUploadResponse {
  id: string;
  is_public?: boolean;
}

/**
 * Build the public, no-auth, fully-qualified URL for a bucket file id.
 *
 * `window.location.origin` resolves to the tenant's own host in every deployed
 * environment. (In local dev this is the Vite dev server on :8000 rather than the API on
 * :10000, so images embedded while developing locally will not resolve in a real mail
 * client — that is a dev-only artifact, not something to "fix" by hardcoding a host.)
 */
function publicBucketUrl(id: string): string {
  return `${window.location.origin}/api3.2/noauth/bucket/${id}`;
}

/**
 * Upload an image to the file bucket as a public file and return its absolute no-auth
 * URL, suitable for embedding directly in an email body.
 *
 * @param file - The image blob/file to upload.
 * @param filename - Name to store the file under (e.g. from a paste or file picker).
 * @returns The public, absolute URL, e.g. `https://tenant.example.com/api3.2/noauth/bucket/<id>`.
 * @throws If no file is given, the upload fails, or the response carries no file id.
 */
export async function uploadEmailImage(file: File | Blob, filename: string): Promise<string> {
  if (!file) {
    throw new Error('uploadEmailImage: a file is required');
  }

  // Fall back to a generic name rather than uploading a nameless file, which some
  // clients display awkwardly. Callers should pass a real name where they have one.
  const safeName = filename && filename.trim() ? filename.trim() : 'email-image';

  const formData = new FormData();
  // The third arg sets the filename for a bare Blob (which has no `name`) and overrides
  // it for a File — both correct here.
  formData.append('file', file, safeName);
  // Email images are public by definition. The value is read as a string on the server.
  formData.append('is_public', 'true');

  try {
    const { data } = await axiosInstance.post<BucketUploadResponse>(
      BUCKET_UPLOAD_ENDPOINT,
      formData
    );

    if (!data || !data.id) {
      // A 2xx with no id would otherwise produce a URL that 404s in every inbox.
      throw new Error('Upload response did not include a file id');
    }

    // The server upgrades an existing private file to public when a public upload is
    // requested, so this should not happen — but if it ever does, the no-auth URL below
    // would 404 for recipients, so surface it loudly rather than shipping broken images.
    if (data.is_public === false) {
      console.warn(
        '[uploadEmailImage] Bucket returned a non-public file; its email URL will not load for recipients:',
        data.id
      );
    }

    return publicBucketUrl(data.id);
  } catch (error) {
    console.error('[uploadEmailImage] Failed to upload email image:', error);
    throw error;
  }
}

export default uploadEmailImage;
